import {
  ArrowRight,
  Building2,
  Check,
  ChevronDown,
  Eye,
  FileText,
  GitBranch,
  Languages,
  MessageSquare,
  Play,
  Receipt,
  ShieldCheck,
  Upload,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { YouTubeFacade } from "@/components/youtube-facade";
import { LocaleLink } from "@/components/locale-link";
import { FaqAccordion } from "@/components/accordion";
import { useTranslation } from "@/common/providers/i18n-provider";
import { AI_FAQ_KEYS } from "@/components/ai-meta";
import { CONTACT_URL, SIGN_UP_LEDGER } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import "@/styles/assistant.css";

const CAPABILITIES = [
  {
    icon: MessageSquare,
    title: "ai.cap.answers.title",
    desc: "ai.cap.answers.desc",
  },
  { icon: Receipt, title: "ai.cap.receipt.title", desc: "ai.cap.receipt.desc" },
  { icon: Upload, title: "ai.cap.import.title", desc: "ai.cap.import.desc" },
  { icon: Check, title: "ai.cap.approve.title", desc: "ai.cap.approve.desc" },
  {
    icon: Languages,
    title: "ai.cap.language.title",
    desc: "ai.cap.language.desc",
  },
] as const;
const STEPS = [
  ["ai.how.step1.title", "ai.how.step1.desc"],
  ["ai.how.step2.title", "ai.how.step2.desc"],
  ["ai.how.step3.title", "ai.how.step3.desc"],
  ["ai.how.step4.title", "ai.how.step4.desc"],
] as const;
const QUESTIONS = [
  "ai.ask.q1",
  "ai.ask.q2",
  "ai.ask.q3",
  "ai.ask.q4",
  "ai.ask.q5",
  "ai.ask.q6",
  "ai.ask.q7",
  "ai.ask.q8",
  "ai.ask.q9",
  "ai.ask.q10",
  "ai.ask.q11",
] as const;
const OWNERSHIP = [
  { icon: FileText, text: "ai.own.b1" },
  { icon: GitBranch, text: "ai.own.b2" },
  { icon: Upload, text: "ai.own.b3" },
  { icon: Eye, text: "ai.own.b4" },
] as const;

/** Shared by /assistant and its /ai alias; metadata stays in ai-meta.ts.
 * Examples are illustrative and never access a ledger. Native disclosure
 * and the click-to-load video need no animation timers. */
export function AiAssistantPage() {
  const { t } = useTranslation();
  return (
    <div className="assistant-page">
      <AppHeader />
      <main id="main">
        <section className="assistant-hero" aria-labelledby="assistant-title">
          <div className="assistant-container assistant-hero-grid">
            <div className="assistant-hero-copy">
              <p className="assistant-eyebrow">
                <MessageSquare size={18} aria-hidden="true" />
                {t("ai.badge")}
              </p>
              <h1 id="assistant-title" className="font-display-brand">
                <span>{t("ai.hero.h1.line1")}</span>{" "}
                <span>{t("ai.hero.h1.line2")}</span>
              </h1>
              <p className="assistant-description">{t("ai.hero.sub")}</p>
              <div className="assistant-actions">
                <a
                  className="assistant-button"
                  href={productUrl(SIGN_UP_LEDGER, "ai_assistant")}
                >
                  {t("ai.hero.cta.primary")}
                  <ArrowRight
                    className="assistant-forward"
                    size={18}
                    aria-hidden="true"
                  />
                </a>
                <a className="assistant-text-link" href="#demo">
                  <Play size={16} aria-hidden="true" />
                  {t("ai.hero.cta.secondary")}
                </a>
              </div>
            </div>
            <figure className="assistant-conversation">
              <div className="assistant-conversation-heading">
                <MessageSquare size={20} aria-hidden="true" />
                <span>{t("ai.cap.answers.title")}</span>
              </div>
              <div className="assistant-conversation-body">
                <blockquote className="assistant-question">
                  <p>{t("ai.hero.chat.q")}</p>
                </blockquote>
                <div className="assistant-answer">
                  <span className="assistant-avatar" aria-hidden="true">
                    <MessageSquare size={20} />
                  </span>
                  <p>{t("ai.hero.chat.a")}</p>
                </div>
                <details className="assistant-activity">
                  <summary>
                    <Eye size={16} aria-hidden="true" />
                    <span>{t("ai.how.step3.title")}</span>
                    <ChevronDown size={16} aria-hidden="true" />
                  </summary>
                  <p>{t("ai.hero.chat.activity")}</p>
                </details>
              </div>
              <figcaption>{t("ai.design.example")}</figcaption>
            </figure>
          </div>
          <ul className="assistant-container assistant-promises">
            {["ai.cta.badge.1", "ai.cta.badge.2", "ai.cta.badge.3"].map(
              (key) => (
                <li key={key}>
                  <Check size={17} aria-hidden="true" />
                  {t(key)}
                </li>
              ),
            )}
          </ul>
        </section>

        <section
          className="assistant-container assistant-section assistant-capabilities"
          aria-labelledby="assistant-capabilities-title"
        >
          <div>
            <p className="assistant-eyebrow">{t("ai.cap.eyebrow")}</p>
            <h2 id="assistant-capabilities-title" className="assistant-heading">
              {t("ai.cap.heading")}
            </h2>
            <div className="assistant-formats">
              <FileText size={24} aria-hidden="true" />
              <p>{t("ai.cap.formats")}</p>
            </div>
          </div>
          <div className="assistant-capability-list">
            {CAPABILITIES.map(({ icon: Icon, title, desc }) => (
              <article key={title}>
                <Icon size={23} strokeWidth={1.6} aria-hidden="true" />
                <div>
                  <h3>{t(title)}</h3>
                  <p>{t(desc)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section
          id="demo"
          tabIndex={-1}
          className="assistant-demo"
          aria-labelledby="assistant-demo-title"
        >
          <div className="assistant-container assistant-section">
            <div className="assistant-section-intro">
              <p className="assistant-eyebrow">{t("ai.how.eyebrow")}</p>
              <h2 id="assistant-demo-title" className="assistant-heading">
                {t("ai.how.heading")}
              </h2>
            </div>
            <div className="assistant-demo-grid">
              <div className="assistant-video">
                <YouTubeFacade
                  videoId="aUBn5oI43c0"
                  title={t("ai.design.videoTitle")}
                  playLabel={t("ai.hero.cta.secondary")}
                />
              </div>
              <ol className="assistant-steps">
                {STEPS.map(([title, desc], index) => (
                  <li key={title}>
                    <span aria-hidden="true">{index + 1}</span>
                    <div>
                      <h3>{t(title)}</h3>
                      <p>{t(desc)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </section>

        <section
          className="assistant-control"
          aria-labelledby="assistant-control-title"
        >
          <div className="assistant-container assistant-section">
            <div className="assistant-control-grid">
              <div>
                <p className="assistant-eyebrow">
                  <ShieldCheck size={18} aria-hidden="true" />
                  {t("ai.control.eyebrow")}
                </p>
                <h2 id="assistant-control-title" className="assistant-heading">
                  {t("ai.control.heading")}
                </h2>
                <p className="assistant-description">{t("ai.control.body")}</p>
              </div>
              <figure className="assistant-review">
                <div className="assistant-review-heading">
                  <GitBranch size={18} aria-hidden="true" />
                  {t("ai.control.card.title")}
                </div>
                <pre dir="ltr">
                  <code>{t("ai.design.transaction")}</code>
                </pre>
                <div className="assistant-review-decisions" aria-hidden="true">
                  <span>
                    <Check size={15} />
                    {t("ai.control.card.approve")}
                  </span>
                  <span>{t("ai.control.card.deny")}</span>
                </div>
                <figcaption>{t("ai.design.example")}</figcaption>
              </figure>
            </div>
            <div className="assistant-ownership">
              <div>
                <p className="assistant-eyebrow">{t("ai.own.eyebrow")}</p>
                <h3>{t("ai.own.heading")}</h3>
              </div>
              <ul>
                {OWNERSHIP.map(({ icon: Icon, text }) => (
                  <li key={text}>
                    <Icon size={19} aria-hidden="true" />
                    <span>{t(text)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section
          className="assistant-container assistant-section assistant-questions"
          aria-labelledby="assistant-questions-title"
        >
          <div className="assistant-section-intro">
            <p className="assistant-eyebrow">{t("ai.ask.eyebrow")}</p>
            <h2 id="assistant-questions-title" className="assistant-heading">
              {t("ai.ask.heading")}
            </h2>
            <p className="assistant-language-note">
              <Languages size={19} aria-hidden="true" />
              {t("ai.ask.note")}
            </p>
          </div>
          <ul className="assistant-question-list">
            {QUESTIONS.map((key) => (
              <li key={key}>
                <MessageSquare size={17} aria-hidden="true" />
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>
          <div className="assistant-audience">
            <div>
              <p className="assistant-eyebrow">
                <Building2 size={18} aria-hidden="true" />
                {t("ai.who.eyebrow")}
              </p>
              <h3>{t("ai.who.heading")}</h3>
            </div>
            <ul>
              {["ai.who.1", "ai.who.2", "ai.who.3", "ai.who.4", "ai.who.5"].map(
                (key) => (
                  <li key={key}>{t(key)}</li>
                ),
              )}
            </ul>
          </div>
        </section>

        <section
          className="assistant-container assistant-pricing"
          aria-labelledby="assistant-pricing-title"
        >
          <div>
            <p className="assistant-eyebrow">{t("ai.pricing.eyebrow")}</p>
            <h2 id="assistant-pricing-title" className="assistant-heading">
              {t("ai.pricing.heading")}
            </h2>
            <p className="assistant-description">{t("ai.pricing.body")}</p>
          </div>
          <LocaleLink to="/pricing" className="assistant-button">
            {t("ai.pricing.cta")}
            <ArrowRight
              className="assistant-forward"
              size={18}
              aria-hidden="true"
            />
          </LocaleLink>
        </section>

        <section
          className="assistant-container assistant-section assistant-faq"
          aria-labelledby="assistant-faq-title"
        >
          <h2 id="assistant-faq-title" className="assistant-heading">
            {t("common.frequentlyAskedQuestions")}
          </h2>
          <FaqAccordion
            items={AI_FAQ_KEYS.map(([questionKey, answerKey]) => ({
              id: questionKey,
              question: t(questionKey),
              answer: t(answerKey),
            }))}
          />
        </section>

        <section
          className="assistant-final"
          aria-labelledby="assistant-final-title"
        >
          <div className="assistant-container">
            <MessageSquare
              className="assistant-final-mark"
              size={56}
              strokeWidth={1.2}
              aria-hidden="true"
            />
            <h2 id="assistant-final-title" className="assistant-heading">
              {t("ai.cta.heading")}
            </h2>
            <p className="assistant-description">{t("ai.cta.sub")}</p>
            <div className="assistant-actions">
              <a
                className="assistant-button"
                href={productUrl(SIGN_UP_LEDGER, "ai_assistant")}
              >
                {t("ai.cta.primary")}
                <ArrowRight
                  className="assistant-forward"
                  size={18}
                  aria-hidden="true"
                />
              </a>
              <a
                className="assistant-text-link"
                href={productUrl(CONTACT_URL, "ai_assistant")}
              >
                {t("ai.cta.secondary")}
                <ArrowRight
                  className="assistant-forward"
                  size={17}
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
